import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuArrowDownWideNarrow, LuArrowUpWideNarrow, LuClock3, LuHourglass, LuImage, LuMessageSquareWarning } from 'react-icons/lu';
import DataCardList from '../components/data/DataCardList';
import DataTable from '../components/data/DataTable';
import DataToolbar from '../components/data/DataToolbar';
import FilterSheet from '../components/data/FilterSheet';
import StatTiles from '../components/data/StatTiles';
import TablePagination from '../components/data/TablePagination';
import ToggleChip from '../components/data/ToggleChip';
import useElementWidth from '../hooks/useElementWidth';
import useMediaQuery from '../hooks/useMediaQuery';
import usePagedList from '../hooks/usePagedList';
import useTableColumns from '../hooks/useTableColumns';
import useViewportFit from '../hooks/useViewportFit';
import useErrorReports from './ErrorReports/useErrorReports';
import useErrorReportText from './ErrorReports/errorReportText';
import {
  ALL_STATUSES,
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  REPORT_COLUMNS,
  REPORT_FILTER_KEYS,
  STATUSES,
  getFilterState,
} from './ErrorReports/errorReportColumns';
import ErrorReportsHeader from './ErrorReports/parts/ErrorReportsHeader';
import ReportCell, { ReportCardRow, useCellTitle } from './ErrorReports/parts/ReportCell';
import ReportDetailPane from './ErrorReports/parts/ReportDetailPane';
import { ReportDetailsDialog, ScreenshotDialog } from './ErrorReports/parts/ReportDialogs';

const COLUMNS_STORAGE_KEY = 'errorReports.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Çalışan Hata Geribildirimleri (Tailwind), built like the other data pages:
 * tiles that filter, a table card that reaches at most the bottom of the
 * screen (header filters, status switch, quick filters, date order, column
 * menu), the status changed right in the row, and the selected report's
 * panel beside the table. The data logic lives in useErrorReports.
 */
const ErrorReportsPage = () => {
  const {
    isLoading, errorReports, filteredReports, stats,
    selectedErrorReport, setSelectedErrorReport,
    searchTerms, handleFilter, generalSearchTerm, setGeneralSearchTerm,
    statusFilter, setStatusFilter, onlyScreenshots, setOnlyScreenshots, onlyRecent, setOnlyRecent,
    sortOrder, toggleSortOrder, clearAllFilters,
    changeStatus, savingStatusId, downloadExcel,
  } = useErrorReports();
  const { language, locale, label, statusLabel } = useErrorReportText();
  const getCellTitle = useCellTitle();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: REPORT_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasLoaded = errorReports.length > 0 || !isLoading;

  // Süzgeç, arama ya da sıra değişince ilk sayfaya dönülür
  const resetKey = JSON.stringify([searchTerms, generalSearchTerm, statusFilter, onlyScreenshots, onlyRecent, sortOrder]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredReports, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  // Escape closes the panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedErrorReport) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedErrorReport(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedErrorReport, showFilterSheet, setSelectedErrorReport]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: REPORT_COLUMNS[key].type ? { type: REPORT_COLUMNS[key].type } : undefined,
  })), [columns.visibleKeys, columnLabel]);

  const filterChips = REPORT_FILTER_KEYS
    .filter((key) => getFilterState(key, searchTerms[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: String(searchTerms[key]), state: 'applied' }));

  const isFilterActive = Object.values(searchTerms).some(Boolean) || Boolean(generalSearchTerm)
    || statusFilter !== ALL_STATUSES || onlyScreenshots || onlyRecent;

  // "Sayfaya git": 1 ile son sayfa arasında olmalı
  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedErrorReport ? filteredReports.findIndex((report) => report.id === selectedErrorReport.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredReports[selectedIndex + offset];
    if (!sibling) return;
    setSelectedErrorReport(sibling);
    reveal(sibling.id);
  };

  const total = stats.total;
  const shareOf = (count) => (total > 0 ? Math.min(1, count / total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: total, icon: LuMessageSquareWarning, gradient: 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30', onClick: clearAllFilters, active: !isFilterActive },
    { key: 'pending', label: label('stats.pending'), shortLabel: label('stats.pendingShort'), value: stats.pending, icon: LuHourglass, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', share: shareOf(stats.pending), shareTitle: shareTitle(stats.pending), barClass: 'tw-bg-gradient-to-r tw-from-amber-300 tw-to-orange-500', onClick: () => setStatusFilter(statusFilter === 'Beklemede' ? ALL_STATUSES : 'Beklemede'), active: statusFilter === 'Beklemede' },
    { key: 'screenshots', label: label('stats.screenshots'), shortLabel: label('stats.screenshotsShort'), value: stats.withScreenshots, icon: LuImage, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30', share: shareOf(stats.withScreenshots), shareTitle: shareTitle(stats.withScreenshots), barClass: 'tw-bg-gradient-to-r tw-from-violet-300 tw-to-purple-500', onClick: () => setOnlyScreenshots(!onlyScreenshots), active: onlyScreenshots },
    { key: 'recent', label: label('stats.recent'), shortLabel: label('stats.recentShort'), value: stats.last24Hours, icon: LuClock3, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', onClick: () => setOnlyRecent(!onlyRecent), active: onlyRecent },
  ];

  const SortIcon = sortOrder === 'asc' ? LuArrowUpWideNarrow : LuArrowDownWideNarrow;
  const quickFilters = (
    <>
      <ToggleChip pressed={onlyScreenshots} onChange={setOnlyScreenshots} icon={LuImage} label={label('toolbar.screenshots')} title={label('toolbar.screenshotsHint')} count={hasLoaded ? stats.withScreenshots : undefined} tone="violet" />
      <ToggleChip pressed={onlyRecent} onChange={setOnlyRecent} icon={LuClock3} label={label('toolbar.recent')} title={label('toolbar.recentHint')} count={hasLoaded ? stats.last24Hours : undefined} tone="teal" />
      <button
        type="button"
        onClick={toggleSortOrder}
        title={label('toolbar.sortHint')}
        className="tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-text-xs tw-font-semibold tw-text-slate-600 tw-transition-colors hover:tw-bg-white hover:tw-text-slate-900 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white"
      >
        <SortIcon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {sortOrder === 'asc' ? label('toolbar.oldest') : label('toolbar.newest')}
      </button>
    </>
  );

  const paneProps = {
    report: selectedErrorReport,
    savingStatus: Boolean(selectedErrorReport) && savingStatusId === selectedErrorReport.id,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredReports.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredReports.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedErrorReport(null),
    onStatusChange: changeStatus,
    onViewScreenshot: setScreenshotUrl,
    onViewDetails: () => setDetailsOpen(true),
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <ErrorReportsHeader onExcel={downloadExcel} />

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: generalSearchTerm,
                onChange: setGeneralSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: [
                  { value: ALL_STATUSES, label: label('status.all') },
                  ...STATUSES.map((value) => ({ value, label: statusLabel(value) })),
                ],
                value: statusFilter,
                onChange: setStatusFilter,
                layoutId: 'error-report-status-filter',
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearAllFilters}
              extra={quickFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(report) => <ReportCardRow report={report} />}
                selectedId={selectedErrorReport?.id}
                onSelect={setSelectedErrorReport}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearAllFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={searchTerms}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(report, key) => (
                  <ReportCell
                    columnKey={key}
                    report={report}
                    savingStatus={savingStatusId === report.id}
                    onStatusChange={changeStatus}
                    onViewScreenshot={setScreenshotUrl}
                  />
                )}
                getCellTitle={getCellTitle}
                selectedId={selectedErrorReport?.id}
                onSelect={setSelectedErrorReport}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearAllFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredReports.length}
              rangeOf={label('table.rangeOf', { total: filteredReports.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <ReportDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <ReportDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={REPORT_FILTER_KEYS.map((key) => ({ key, label: columnLabel(key), type: 'text' }))}
        filters={searchTerms}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearAllFilters}
        resultCount={filteredReports.length}
      />

      <ReportDetailsDialog
        report={selectedErrorReport}
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onViewScreenshot={setScreenshotUrl}
      />

      <ScreenshotDialog url={screenshotUrl} onClose={() => setScreenshotUrl(null)} />
    </>
  );
};

export default ErrorReportsPage;
